import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  Avatar,
  LinearProgress,
  useTheme,
  alpha,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  People as PeopleIcon,
  HowToReg as ApprovedIcon,
  Pending as PendingIcon,
  Article as NewsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Schedule as ScheduleIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useSettings } from '../../../contexts/SettingsContext';
import { useTranslation } from 'react-i18next';

const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

interface DegreeStat {
  code: string;
  name_th: string;
  name_en: string;
  level: number;
  count: number;
}

interface SystemOverview {
  users: { total: number };
  alumni: { approved: number; pending: number };
  news: { published: number; draft: number };
  behavior_events: { total: number };
  by_graduation_year: Record<string, number>;
  by_active_degree: DegreeStat[];
}

function Dashboard() {
  const theme = useTheme();
  const { settings } = useSettings();
  const { t } = useTranslation();
  const [overview, setOverview] = useState<SystemOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/reports/system/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverview(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch overview');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const StatCard = ({ 
    title, 
    value, 
    icon, 
    color, 
    trend 
  }: { 
    title: string; 
    value: number; 
    icon: React.ReactNode; 
    color: string;
    trend?: { value: number; isPositive: boolean };
  }) => (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {title}
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {value}
              </Typography>
              {trend && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {trend.isPositive ? (
                    <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      color: trend.isPositive ? 'success.main' : 'error.main',
                      fontWeight: 600,
                    }}
                  >
                    {trend.value}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('dashboard.vsLastYear')}
                  </Typography>
                </Stack>
              )}
            </Box>
            <Avatar
              sx={{
                bgcolor: alpha(color, 0.1),
                color: color,
                width: 56,
                height: 56,
              }}
            >
              {icon}
            </Avatar>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t('dashboard.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.welcomeMessage')}
          </Typography>
        </Box>
        {settings && (
          <Chip
            icon={<ScheduleIcon />}
            label={`${t('dashboard.timezone')}: ${settings.timezone}`}
            size="small"
            variant="outlined"
            color="primary"
          />
        )}
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('dashboard.totalAlumni')}
            value={overview?.alumni.approved || 0}
            icon={<PeopleIcon sx={{ fontSize: 28 }} />}
            color={theme.palette.primary.main}
            trend={{ value: 12.5, isPositive: true }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('dashboard.approvedAlumni')}
            value={overview?.alumni.approved || 0}
            icon={<ApprovedIcon sx={{ fontSize: 28 }} />}
            color={theme.palette.success.main}
            trend={{ value: 8.2, isPositive: true }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('dashboard.pendingApprovals')}
            value={overview?.alumni.pending || 0}
            icon={<PendingIcon sx={{ fontSize: 28 }} />}
            color={theme.palette.warning.main}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('dashboard.publishedNews')}
            value={overview?.news.published || 0}
            icon={<NewsIcon sx={{ fontSize: 28 }} />}
            color={theme.palette.info.main}
            trend={{ value: 5.1, isPositive: true }}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                {t('dashboard.systemOverview')}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        {t('dashboard.alumniApprovalRate')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {overview?.alumni.approved && overview?.alumni.pending
                          ? Math.round(
                              (overview.alumni.approved / 
                              (overview.alumni.approved + overview.alumni.pending)) * 100
                            )
                          : 0}%
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={
                        overview?.alumni.approved && overview?.alumni.pending
                          ? (overview.alumni.approved / 
                            (overview.alumni.approved + overview.alumni.pending)) * 100
                          : 0
                      }
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'success.main',
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        {t('dashboard.contentPublished')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {overview?.news.published && overview?.news.draft
                          ? Math.round(
                              (overview.news.published / 
                              (overview.news.published + overview.news.draft)) * 100
                            )
                          : 0}%
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={
                        overview?.news.published && overview?.news.draft
                          ? (overview.news.published / 
                            (overview.news.published + overview.news.draft)) * 100
                          : 0
                      }
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'info.main',
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                {t('dashboard.activityStats')}
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.draftNews')}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {overview?.news.draft || 0}
                    </Typography>
                  </Stack>
                </Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.behaviorEvents')}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {overview?.behavior_events.total || 0}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Alumni by Graduation Year */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    width: 40,
                    height: 40,
                  }}
                >
                  <CalendarIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('dashboard.alumniByGraduationYear')}
                </Typography>
              </Stack>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('dashboard.year')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{t('dashboard.alumniCount')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{t('dashboard.percentage')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overview?.by_graduation_year && Object.entries(overview.by_graduation_year)
                      .sort(([a], [b]) => Number(b) - Number(a))
                      .slice(0, 10)
                      .map(([year, count]) => {
                        const percentage = overview.alumni.approved > 0
                          ? ((count as number / overview.alumni.approved) * 100).toFixed(1)
                          : '0.0';
                        return (
                          <TableRow key={year} hover>
                            <TableCell>{year}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={count}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">{percentage}%</TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              {overview?.by_graduation_year && Object.keys(overview.by_graduation_year).length > 10 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', textAlign: 'center', mt: 2 }}
                >
                  {t('dashboard.showingTopYears')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Alumni by Active Degree */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    width: 40,
                    height: 40,
                  }}
                >
                  <SchoolIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('dashboard.alumniByActiveDegree')}
                </Typography>
              </Stack>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('dashboard.degree')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{t('dashboard.alumniCount')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{t('dashboard.percentage')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overview?.by_active_degree && overview.by_active_degree
                      .slice(0, 10)
                      .map((degree) => {
                        const percentage = overview.alumni.approved > 0
                          ? ((degree.count / overview.alumni.approved) * 100).toFixed(1)
                          : '0.0';
                        return (
                          <TableRow key={degree.code} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {degree.name_th}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {degree.name_en}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={degree.count}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">{percentage}%</TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              {overview?.by_active_degree && overview.by_active_degree.length > 10 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', textAlign: 'center', mt: 2 }}
                >
                  {t('dashboard.showingTopDegrees')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
